import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraEmpty } from "../../../../components/AtiraEmpty";
import { Flex } from "../../../../components/Flex";
import { SubHeader } from "../../../../components/SubHeader";
import { Text } from "../../../../components/Text";
import { WarningModal } from "../../../../components/WarningModal";
import { RoadmapItem } from "../../../../model/roadmap/RoadmapItem";
import { adminSliceSelectors } from "../../../../redux/admin/admin.selector";
import { adminActions } from "../../../../redux/admin/admin.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { TestIDs } from "../../../../tests/types/testIds";
import { PageIcons } from "../../../../utils/GlobalIcons";
import { AdminRoadmapItemCard } from "./components/AdminRoadmapItemCard";
import { AdminRoadmapItemCreateDrawer } from "./components/AdminRoadmapItemCreateDrawer";
import { AdminRoadmapItemUpdateDrawer } from "./components/AdminRoadmapItemUpdateDrawer";
import { RoadmapItemStatus } from "../../../../model/roadmap/types/RoadmapItemStatus.enum";

const ROADMAP_STATUS_ORDER = [
  RoadmapItemStatus.UPCOMING,
  RoadmapItemStatus.IN_PROGRESS,
  RoadmapItemStatus.DONE,
];

const Container = styled(Flex)`
  flex: 1;
  flex-direction: column;
`;

const AdminRoadmap: React.FC = () => {
  const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RoadmapItem | null>(null);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const roadmapItems = useAppSelector(adminSliceSelectors.selectRoadmapItems);

  const groups = ROADMAP_STATUS_ORDER.map((status) => ({
    status,
    items: (roadmapItems || []).filter(
      (item: RoadmapItem) => item.status === status,
    ),
  })).filter((group) => group.items.length);

  const onEdit = (roadmapItem: RoadmapItem) => {
    setSelectedItem(roadmapItem);
    setEditDrawerVisible(true);
  };

  const onDelete = (roadmapItem: RoadmapItem) => {
    setSelectedItem(roadmapItem);
    setDeleteModalVisible(true);
  };

  const deleteRoadmapItem = async () => {
    try {
      if (!selectedItem) {
        return;
      }

      setDeleteLoading(true);

      await dispatch(
        adminActions.deleteRoadmapItem({
          roadmapItemId: selectedItem._id,
          userId,
        }),
      ).unwrap();
      await dispatch(adminActions.getRoadmapItems({ userId })).unwrap();

      setDeleteModalVisible(false);

      AtiraToast.success(t("admin.roadmap.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    dispatch(adminActions.getRoadmapItems({ userId }));
  }, [dispatch, userId]);

  return (
    <Container>
      <SubHeader
        title={t("admin.roadmap.widget.title")}
        icon={PageIcons.AdminRoadmap}
        actions={[
          {
            title: t("common.create"),
            icon: faPlus,
            onClick: () => setCreateDrawerVisible(true),
            testId: TestIDs.roadmap.subHeader.create,
          },
        ]}
      />

      <Flex
        flexDirection="column"
        gap="m"
        padding="m"
        paddingTop="none"
        flex={1}
      >
        <Text fontWeight={"bold"}>{t("admin.roadmap.route.title")}</Text>

        {roadmapItems?.length ? (
          groups.map((g) =>
            g.items.map((roadmapItem) => (
              <AdminRoadmapItemCard
                key={roadmapItem._id}
                roadmapItem={roadmapItem}
                onEdit={() => onEdit(roadmapItem)}
                onDelete={() => onDelete(roadmapItem)}
              />
            )),
          )
        ) : (
          <AtiraEmpty
            description={t("admin.roadmap.empty")}
            action={{
              title: t("common.create"),
              onClick: () => setCreateDrawerVisible(true),
              dataTestId: TestIDs.roadmap.empty.create,
            }}
          />
        )}
      </Flex>

      <AdminRoadmapItemCreateDrawer
        open={createDrawerVisible}
        onClose={() => setCreateDrawerVisible(false)}
      />

      <AdminRoadmapItemUpdateDrawer
        open={editDrawerVisible}
        onClose={() => setEditDrawerVisible(false)}
        roadmapItem={selectedItem}
      />

      <WarningModal
        title={t("admin.roadmap.delete.title")}
        description={t("admin.roadmap.delete.description")}
        isOpen={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={deleteRoadmapItem}
        loading={deleteLoading}
        confirmButtonDataTestId={TestIDs.roadmap.card.deleteConfirm}
      />
    </Container>
  );
};

export default AdminRoadmap;
